import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, Star, CheckCircle } from "lucide-react";

interface SeoPageLayoutProps {
  title: string;
  children: React.ReactNode;
  subtitle?: string;
  metaDescription?: string;
}

const SeoPageLayout = ({ title, children, subtitle, metaDescription }: SeoPageLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const canonicalUrl = `https://wystepkabaretu.pl${location.pathname}`;
  
  const defaultDescription = metaDescription || `${title} - profesjonalne występy kabaretowe dla firm. Ponad 500 zrealizowanych eventów. Zamów kabaret na swoje wydarzenie!`;

  const scrollToContact = () => {
    navigate("/", { state: { scrollTo: "kontakt" } });
  };

  // JSON-LD Schema for LocalBusiness + Service
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "name": "WystepKabaretu.pl",
        "url": "https://wystepkabaretu.pl",
        "telephone": "+48533333993",
        "email": "kontakt@wystepkabaretu.pl",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "ul. Księcia Witolda 49/15",
          "addressLocality": "Wrocław",
          "postalCode": "50-202",
          "addressCountry": "PL"
        },
        "priceRange": "$$",
        "areaServed": {
          "@type": "Country",
          "name": "Polska"
        }
      },
      {
        "@type": "Service",
        "name": title,
        "description": defaultDescription,
        "provider": {
          "@type": "LocalBusiness",
          "name": "WystepKabaretu.pl"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Polska"
        },
        "serviceType": "Występy kabaretowe"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title} | WystepKabaretu.pl</title>
        <meta name="description" content={defaultDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${title} | WystepKabaretu.pl`} />
        <meta property="og:description" content={defaultDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pl_PL" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-primary pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_hsl(195_90%_50%_/_0.15),_transparent_60%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
        <div className="container max-w-5xl px-4 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-accent fill-accent" />
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">Profesjonalna rozrywka</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-montserrat font-black text-primary-foreground mb-6 leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg md:text-xl text-primary-foreground/70 max-w-2xl">{subtitle}</p>
          )}
          <div className="flex flex-wrap gap-4 mt-8">
            <Button
              onClick={scrollToContact}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full font-semibold text-base px-8 py-6 shadow-lg shadow-accent/25"
            >
              Zamów występ <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <a
              href="tel:+48533333993"
              className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors font-medium bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10"
            >
              <Phone className="w-4 h-4" /> +48 533 333 993
            </a>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-secondary/50 border-b border-border/50">
        <div className="container max-w-5xl px-4 py-4">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-accent" /> Ponad 500 zrealizowanych eventów</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-accent" /> Profesjonalni artyści</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-accent" /> Cała Polska</span>
          </div>
        </div>
      </section>

      {/* Main content */}
      <main className="container max-w-4xl py-16 px-4">
        <article className="seo-content">
          {children}
        </article>

        {/* Bottom CTA */}
        <div className="mt-16 relative overflow-hidden rounded-2xl bg-primary p-8 md:p-12 text-center">
          <div className="absolute inset-0 animated-gradient opacity-80" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_hsl(195_90%_50%_/_0.2),_transparent_60%)]" />
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-montserrat font-bold text-primary-foreground mb-4">
              Zainteresowany? Porozmawiajmy!
            </h2>
            <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
              Skontaktuj się z nami i opowiedz o swoim wydarzeniu. Przygotujemy spersonalizowaną ofertę dopasowaną do Twoich potrzeb.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={scrollToContact}
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full font-semibold text-lg px-10 py-6 shadow-lg shadow-accent/25"
              >
                Napisz do nas <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a
                href="tel:+48533333993"
                className="inline-flex items-center gap-2 text-primary-foreground hover:text-accent transition-colors font-semibold text-lg px-6 py-3"
              >
                <Phone className="w-5 h-5" /> Zadzwoń teraz
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SeoPageLayout;
